import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type AxiosError } from 'axios'

import { assessmentProcessService } from '@/services/api/assessment_process.service'
import type {
  Evaluation,
  EvaluationCreate,
  EvaluationUpdate
} from '@/types/assessment_process.types'
import {
  evaluationQueryKeys,
  evaluationQueryUtils
} from '@/utils/query/assessments/evaluationQueryClient.utils'
import { errorUtils } from '@/utils/query/common'

// =============================================================================
// CREATE EVALUATION
// =============================================================================

export interface UseCreateEvaluationOptions {
  onSuccess?: (evaluation: Evaluation) => void
  onError?: (error: Error) => void
}

export function useCreateEvaluation(
  assessmentId: string,
  options: UseCreateEvaluationOptions = {}
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: EvaluationCreate) =>
      assessmentProcessService.createEvaluation(assessmentId, data),

    onMutate: async (newEvaluation) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: evaluationQueryKeys.evaluations.byAssessment(assessmentId)
      })

      // Snapshot the previous value
      const previousEvaluations = queryClient.getQueryData(
        evaluationQueryKeys.evaluations.byAssessment(assessmentId)
      )

      // Optimistically update to the new value
      queryClient.setQueryData(
        evaluationQueryKeys.evaluations.byAssessment(assessmentId),
        (old: Evaluation[] | undefined) => {
          if (!old) return old

          const optimisticEvaluation: Evaluation = {
            ...newEvaluation,
            id: `temp-${Date.now()}`,
            assessment_id: assessmentId,
            evaluador_id: '', // Will be set by server
            evaluado: false,
            brecha: 0,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          } as Evaluation

          return [...old, optimisticEvaluation]
        }
      )

      // Return a context object with the snapshotted value
      return { previousEvaluations }
    },

    onSuccess: async (evaluation) => {
      // Invalidate and refetch
      await evaluationQueryUtils.invalidateEvaluationQueries(queryClient, assessmentId)

      // Show success toast
      // toast.success('Evaluación creada exitosamente')

      options.onSuccess?.(evaluation)
    },

    onError: (error, _, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousEvaluations) {
        queryClient.setQueryData(
          evaluationQueryKeys.evaluations.byAssessment(assessmentId),
          context.previousEvaluations
        )
      }

      // Show error toast
      // const errorMessage = errorUtils.getError(error)
      // toast.error(errorMessage || 'Error al crear la evaluación')

      options.onError?.(error as Error)
    },

    retry: 1,
    retryDelay: 1000
  })
}

// =============================================================================
// UPDATE EVALUATION
// =============================================================================

export interface UseUpdateEvaluationOptions {
  onSuccess?: (evaluation: Evaluation) => void
  onError?: (error: Error | AxiosError) => void
}

export function useUpdateEvaluation(
  assessmentId: string,
  options: UseUpdateEvaluationOptions = {}
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ evaluationId, data }: { evaluationId: string; data: EvaluationUpdate }) =>
      assessmentProcessService.updateEvaluation(assessmentId, evaluationId, data),

    onMutate: async ({ evaluationId, data }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: evaluationQueryKeys.evaluations.detail(assessmentId, evaluationId)
      })
      await queryClient.cancelQueries({
        queryKey: evaluationQueryKeys.evaluations.byAssessment(assessmentId)
      })

      // Snapshot the previous values
      const previousEvaluation = queryClient.getQueryData(
        evaluationQueryKeys.evaluations.detail(assessmentId, evaluationId)
      )
      const previousEvaluations = queryClient.getQueryData(
        evaluationQueryKeys.evaluations.byAssessment(assessmentId)
      )

      // Calculate brecha if nivel_actual and nivel_objetivo are provided
      let calculatedData = { ...data } as Partial<Evaluation>
      if (data.nivel_actual !== undefined || data.nivel_objetivo !== undefined) {
        const currentEvaluation = queryClient.getQueryData<Evaluation>(
          evaluationQueryKeys.evaluations.detail(assessmentId, evaluationId)
        )
        if (currentEvaluation) {
          const nivelActual = data.nivel_actual ?? currentEvaluation.nivel_actual ?? 0
          const nivelObjetivo = data.nivel_objetivo ?? currentEvaluation.nivel_objetivo ?? 0
          calculatedData = {
            ...calculatedData,
            brecha: nivelObjetivo - nivelActual
          }
        }
      }

      // Optimistically update the evaluation
      evaluationQueryUtils.setEvaluationOptimistic(
        queryClient,
        assessmentId,
        evaluationId,
        calculatedData as Partial<Evaluation>
      )

      // Return a context with the previous values
      return { previousEvaluation, previousEvaluations }
    },

    onSuccess: async (evaluation) => {
      // Invalidate and refetch
      await evaluationQueryUtils.invalidateEvaluationQueries(queryClient, assessmentId)

      // Show success toast (implementation ready for toast library)
      // toast.success('Evaluación actualizada exitosamente')

      options.onSuccess?.(evaluation)
    },

    onError: (error, { evaluationId }, context) => {
      // If the mutation fails, use the context to roll back
      if (context?.previousEvaluation) {
        queryClient.setQueryData(
          evaluationQueryKeys.evaluations.detail(assessmentId, evaluationId),
          context.previousEvaluation
        )
      }
      if (context?.previousEvaluations) {
        queryClient.setQueryData(
          evaluationQueryKeys.evaluations.byAssessment(assessmentId),
          context.previousEvaluations
        )
      }

      console.error('Error updating evaluation:', errorUtils.transformAxiosError(error))
      options.onError?.(error)
      // If available, log more error details
      // if (
      //   typeof error === 'object' &&
      //   error !== null &&
      //   'isAxiosError' in error &&
      //   (error as AxiosError).response?.data
      // ) {
      //   console.error('Error:', errorUtils.getErrorMessage(error as AxiosError))
      //   const axiosError = error as AxiosError<HTTPException>
      //   console.error('Error details:', axiosError.code)
      //   console.error('Error details:', axiosError.response?.data?.detail)
      //   // console.error('Error details:', (axiosError.response?.data as any)?.detail)
      //   options.onError?.(error as AxiosError)
      // } else {
      //   console.error('Error details:', error)
      //   options.onError?.(error as Error)
      // }

      // Show error toast
      // const errorMessage = errorUtils.getError(error)
      // toast.error(errorMessage || 'Error al actualizar la evaluación')
    },

    retry: 1,
    retryDelay: 1000
  })
}
